import { MainApi } from 'api/MainApi'
import { StationApi } from 'api/StationApi'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import { Point } from 'geojson'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSuppressNotificationSandwich } from 'hooks/useSuppressNotificationSandwich'
import { NOTIFICATION_SANDWICH_SUPPRESS } from 'utils/notificationSandwichGate'
import Modal from 'react-modal'
import { ILot, IStation } from 'utils/interfaces'

import {
  defaultLotNameForStation,
  isValidStationId,
  isValidStationName,
  normalizeStationId,
  resolveStationTypeFromLots,
  STATION_TYPE_TO_LOT_MODEL,
  StationFormType,
} from '../stationTypeUtils'
import StationView from './StationView/StationView'

import style from './AddStationModal.module.css'

interface IAddStationModal {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  onStationsChanged: () => void
  className?: string
  stationExisting?: IStation
  stationLots?: ILot[]
  stations: IStation[]
  /** Preselect facility on create (e.g. Facility → Assets). */
  defaultFacilityId?: number
}

const defaultStation: IStation = {
  id: 0,
  station_id: '',
  name: '',
  online: false,
}

const AddStationModal: React.FC<IAddStationModal> = props => {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const [facilities, setFacilities] = useState<Array<{ id: number; name: string }>>([])
  const [facilityId, setFacilityId] = useState<number | null>(null)
  const isEditMode = Boolean(props.stationExisting)
  useSuppressNotificationSandwich(props.isOpen, NOTIFICATION_SANDWICH_SUPPRESS.STATION_GEO)

  const selectedStation = useMemo(
    () => props.stations.find((station: { id: number }) => station.id === props.stationExisting?.id),
    [props.stations, props.stationExisting?.id]
  )

  const [tempStation, setTempStation] = useState<IStation>(selectedStation || { ...defaultStation })
  const [stationType, setStationType] = useState<StationFormType | undefined>(() => {
    if (props.stationExisting && props.stationLots?.length) {
      return resolveStationTypeFromLots(props.stationLots)
    }
    return undefined
  })

  useEffect(() => {
    if (!props.isOpen) {
      return
    }
    MainApi.fetchFacilities(1, 500).then(response => {
      setFacilities(
        (response.results ?? []).map((facility: { id: number; name: string }) => ({
          id: facility.id,
          name: facility.name,
        }))
      )
    })
  }, [props.isOpen])

  // Seed only when the modal opens / switches entity — not when Assets/Facility poll refreshes stations/lots.
  useEffect(() => {
    if (!props.isOpen) {
      return
    }
    const station = selectedStation || props.stationExisting || { ...defaultStation }
    setTempStation(station)
    if (props.stationExisting) {
      const lots =
        props.stationLots?.filter(lot => lot.robot_station === props.stationExisting?.id) ?? props.stationLots ?? []
      setStationType(resolveStationTypeFromLots(lots))
      setFacilityId(station.facility_id ?? null)
    } else {
      setStationType(undefined)
      setFacilityId(props.defaultFacilityId ?? null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: ignore poll identity changes of stations/lots
  }, [props.isOpen, props.stationExisting?.id, props.defaultFacilityId])

  const stationIdError =
    !isEditMode && Boolean(tempStation.station_id?.trim()) && !isValidStationId(tempStation.station_id)
  const stationNameError = Boolean(tempStation.name?.trim()) && !isValidStationName(tempStation.name)

  const onSaveClick = async () => {
    setSaving(true)
    try {
      if (!tempStation?.point) {
        throw new Error('Station point should exist')
      }
      if (!isValidStationName(tempStation.name)) {
        throw new Error('Station name is invalid')
      }
      if (props.stationExisting) {
        await StationApi.patchStation(
          props.stationExisting.id,
          tempStation.name,
          props.stationExisting.station_id,
          tempStation.point,
          facilityId
        )
      } else {
        if (!stationType || !isValidStationId(tempStation.station_id)) {
          throw new Error('Station ID and type are required')
        }
        const created = await StationApi.createStation(
          tempStation.name,
          normalizeStationId(tempStation.station_id),
          tempStation.point,
          facilityId
        )
        const lotModel = STATION_TYPE_TO_LOT_MODEL[stationType]
        try {
          await StationApi.createLot({
            name: defaultLotNameForStation(tempStation.name),
            lot_id: 1,
            lot_model: lotModel,
            robot_station_id: created.id,
          })
        } catch (lotError) {
          console.warn('Station created but bootstrap lot failed', lotError)
          throw lotError
        }
        props.onStationsChanged()
      }
      props.onSave()
      props.onClose()
    } catch (error) {
      console.warn('Error creating station', error)
    }
    setSaving(false)
  }

  const restoreDraftFromProps = () => {
    if (props.stationExisting) {
      const station = selectedStation || props.stationExisting
      setTempStation(station)
      const lots =
        props.stationLots?.filter(lot => lot.robot_station === props.stationExisting?.id) ??
        props.stationLots ??
        []
      setStationType(resolveStationTypeFromLots(lots))
      setFacilityId(station.facility_id ?? null)
      return
    }
    setTempStation({ ...defaultStation })
    setStationType(undefined)
    setFacilityId(props.defaultFacilityId ?? null)
  }

  const onCancelClick = () => {
    restoreDraftFromProps()
    props.onClose()
  }

  const onFacilityChanged = (nextFacilityId: number | null) => {
    setFacilityId(nextFacilityId)
    // Reset pin so camera frames the new facility instead of the previous placement.
    setTempStation(prev => (prev.point ? { ...prev, point: null } : prev))
  }

  const onStationNameChanged = (name: string) => {
    setTempStation(prev => ({ ...prev, name }))
  }

  const onStationSNChanged = (station_id: string) => {
    setTempStation(prev => ({ ...prev, station_id: normalizeStationId(station_id) }))
  }

  const onStationPointChanged = (point: Point) => {
    setTempStation(prev => ({ ...prev, point }))
  }

  const isButtonsDisabled =
    !tempStation?.point ||
    !isValidStationName(tempStation.name || '') ||
    (isEditMode ? false : !stationType || !isValidStationId(tempStation.station_id || ''))

  return (
    <Modal
      isOpen={props.isOpen}
      onRequestClose={onCancelClick}
      className={clsx(style.root, props.className)}
      shouldCloseOnOverlayClick={true}
      style={{ overlay: { zIndex: 100 } }}
      ariaHideApp={false}
    >
      <div className={style.titleBlock}>
        <div className={style.title}>{props.stationExisting ? 'Edit Station' : 'New Station'}</div>
      </div>
      <StationView
        onNameChanged={onStationNameChanged}
        onStationSNChanged={onStationSNChanged}
        onStationTypeChanged={setStationType}
        onPointChanged={onStationPointChanged}
        onFacilityChanged={onFacilityChanged}
        station={tempStation}
        stationType={stationType}
        stationLots={props.stationLots}
        isEditMode={isEditMode}
        stationIdError={stationIdError}
        stationNameError={stationNameError}
        facilities={facilities}
        facilityId={facilityId}
        dialogOpen={props.isOpen}
        cameraResetKey={`${props.isOpen}-${isEditMode ? 'edit' : 'create'}-${props.stationExisting?.id ?? 0}-f${facilityId ?? 0}`}
      />

      <div className={style.footer}>
        <Button.Outlined className={style.btn} onClick={onCancelClick}>
          {t('common.cancel')}
        </Button.Outlined>
        <Button.Standart
          className={style.btn}
          disabled={saving || isButtonsDisabled}
          loading={saving}
          onClick={onSaveClick}
        >
          {t('common.save')}
        </Button.Standart>
      </div>
    </Modal>
  )
}

export default AddStationModal
